// @ts-nocheck

import cv, { Mat } from 'opencv-ts'
import {downloadModel, ensureModel, getModel, modelExists} from "../../../utils/model";
import {ModelTypeConstant} from "../../../constants";
import ort from "onnxruntime-web/all";
import {prettierLog} from "../../../utils";
import {getBrowserCapabilities} from "../../../utils/browserUtil";
import {loadImage} from "../../../utils/filesUtil";

let model: any = null;

let modelBasicInfo = getModel(ModelTypeConstant.IN_PAINT_);

let targetWidth = modelBasicInfo.targetWidth;
let targetHeight = modelBasicInfo.targetHeight;
let imageInputName = modelBasicInfo.imageInputName;
let maskInputName = modelBasicInfo.maskInputName;
let channelNum = modelBasicInfo.channelNum;
let maskChannelNum = modelBasicInfo.maskChannelNum;

let loadingNotification = () => {};
const configEnv = (capabilities: {
    webGPU: boolean;
    wasm?: boolean;
    simd: any;
    threads: any;
}) => {
    if (capabilities.webGPU) {
        ort.env.wasm.numThreads = 1;
    } else {
        if (capabilities.threads) {
            ort.env.wasm.numThreads = navigator.hardwareConcurrency ?? 4;// 根据 CPU 核心数动态分配线程
        }
        if (capabilities.simd) {
            ort.env.wasm.simd = true;// 启用 SIMD 指令加速
        }
        ort.env.wasm.proxy = true;// 启用代理模式优化资源加载
    }
}

export const createInferenceSession = async (showAlert: any) => {
    prettierLog({
        msg: 'create InferenceSession',
        type: 'info'
    });
    if(!model) {
        const capabilities = await getBrowserCapabilities();
        configEnv(capabilities);

        const modelBuffer = await downloadModel(ModelTypeConstant.IN_PAINT_, downloadProgressHandle, showAlert);
        model = await ort.InferenceSession.create(modelBuffer, {
            // executionProviders: ['wasm'],
            executionProviders: [capabilities.webGPU ? 'webgpu' : 'wasm'],
            sessionOptions: {
                enableMemPattern: false,  // 禁用内存优化模式
                enableCpuMemArena: true,   // 启用CPU内存池
                memoryLimit: 12048,
            }
        });
    }
    prettierLog({
        msg: 'InferenceSession created',
        type: 'success'
    });
}

export const recreateInferenceSession = async (showAlert: any, showLoadingNotification: any) => {
    if (model && model.handler && model.handler.dispose) {
        model.handler.dispose();
        prettierLog({
            msg: "model dispose",
            type: "success"
        });
    }
    if (model && model.ioBinding) {
        model.ioBinding.dispose(); // 释放绑定的 GPU 缓冲区
        prettierLog({
            msg: "GPU Buffer dispose",
            type: "success"
        });
    }
    model = null;
    modelBasicInfo = getModel(ModelTypeConstant.IN_PAINT_);

    targetWidth = modelBasicInfo.targetWidth;
    targetHeight = modelBasicInfo.targetHeight;
    imageInputName = modelBasicInfo.imageInputName;
    maskInputName = modelBasicInfo.maskInputName;
    channelNum = modelBasicInfo.channelNum;
    maskChannelNum = modelBasicInfo.maskChannelNum;
    loadingNotification = showLoadingNotification;

    await createInferenceSession(showAlert);
}

const downloadProgressHandle = (progressNumber: number) => {
    // console.log("当前下载进度" + progressNumber);
    loadingNotification(progressNumber.toFixed(0), "Model Loading...", "Model Loaded");
}

const resizeMark = (
    image: HTMLImageElement,
    width: number,
    height: number
): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            reject(new Error('Unable to get canvas context'));
            return;
        }
        ctx.drawImage(image, 0, 0, width, height);

        // 获取调整大小后的图片URL
        const resizedImageUrl = canvas.toDataURL();

        // 创建一个新的Image对象并设置其src为调整大小后的图片URL
        const resizedImage = new Image();
        resizedImage.onload = () => resolve(resizedImage);
        resizedImage.onerror = () =>
            reject(new Error('Failed to load resized image'))
        resizedImage.src = resizedImageUrl;
    })
}
function processMark(
    img: HTMLImageElement,
): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
        try {
            const src = cv.imread(img)
            const src_grey = new cv.Mat()

            // 将图像从RGBA转换为二值化
            cv.cvtColor(src, src_grey, cv.COLOR_BGR2GRAY)

            const channels = new cv.MatVector()
            cv.split(src_grey, channels) // 分割通道

            const C = 1 // 通道数
            const H = src_grey.rows // 图像高度
            const W = src_grey.cols // 图像宽度
            const chwArray = new Uint8Array(C * H * W) // 创建新的数组来存储转换后的数据

            for (let c = 0; c < C; c++) {
                const channelData = channels.get(0).data // 获取单个通道的数据
                for (let h = 0; h < H; h++) {
                    for (let w = 0; w < W; w++) {
                        chwArray[c * H * W + h * W + w] = (channelData[h * W + w] !== 255) * 255
                    }
                }
            }
            channels.delete(); // 清理内存
            src.delete();

            resolve(chwArray)
        } catch (error) {
            reject(error)
        }
    })
}
const processImage = (
    img: HTMLImageElement,
): Promise<Uint8Array> => {
    return new Promise((resolve, reject) => {
        try {
            const src = cv.imread(img)
            const src_rgb = new cv.Mat()
            // 将图像从RGBA转换为RGB
            cv.cvtColor(src, src_rgb, cv.COLOR_RGBA2RGB)
            const channels = new cv.MatVector()
            cv.split(src_rgb, channels) // 分割通道

            const C = channels.size() // 通道数
            const H = src_rgb.rows // 图像高度
            const W = src_rgb.cols // 图像宽度
            const chwArray = new Uint8Array(C * H * W) // 创建新的数组来存储转换后的数据

            for (let c = 0; c < C; c++) {
                const channelData = channels.get(c).data // 获取单个通道的数据
                for (let h = 0; h < H; h++) {
                    for (let w = 0; w < W; w++) {
                        chwArray[c * H * W + h * W + w] = channelData[h * W + w]
                    }
                }
            }
            channels.delete(); // 清理内存
            src.delete();
            src_rgb.delete();

            resolve(chwArray); // 返回转换后的数据
        } catch (error) {
            reject(error)
        }
    })
}

const preProcess = async (image: HTMLImageElement, maskBase64: string) => {
    prettierLog({
        msg: 'start preprocess',
        type: 'info'
    });

    targetWidth = image.width;
    targetHeight = image.height;

    const maskImage  = await loadImage(maskBase64);

    const img = await processImage(image);
    const mask = await processMark(
        await resizeMark(maskImage, targetWidth, targetHeight)
    );

    prettierLog({
        msg: 'preprocess end',
        type: 'success'
    });
    return {img, mask};
}

const runModel = async (
    imgUint8Array: Uint8Array,
    maskUint8Array: Uint8Array
) => {
    prettierLog({
        msg: 'start run',
        type: 'info'
    });

    const imageInput = new ort.Tensor('uint8', imgUint8Array, [1, channelNum, targetHeight, targetWidth])
    const maskInput = new ort.Tensor('uint8', maskUint8Array, [1, maskChannelNum, targetHeight, targetWidth])
    const results = await model.run({
        [imageInputName]: imageInput,
        [maskInputName]: maskInput,
    });

    prettierLog({
        msg: 'run end',
        type: 'success'
    });

    // console.log(results);
    // console.log(model.outputNames);
    return results;
}

const postProcess = (results: any) => {
    prettierLog({
        msg: 'start postProcess',
        type: 'info'
    });

    const outputData = results[model.outputNames[0]].data;

    const chwToHwcData = []
    const size = targetWidth * targetHeight
    for (let h = 0; h < targetHeight; h++) {
        for (let w = 0; w < targetWidth; w++) {
            for (let c = 0; c < 3; c++) {
                // RGB通道
                const chwIndex = c * size + h * targetWidth + w;
                let pixelVal = outputData[chwIndex];
                if (pixelVal > 255) {
                    pixelVal = 255;
                } else if (pixelVal < 0) {
                    pixelVal = 0;
                }
                chwToHwcData.push(pixelVal); // 归一化反转
            }
            chwToHwcData.push(255); // Alpha通道
        }
    }

    const imageData = new ImageData(
        new Uint8ClampedArray(chwToHwcData),
        targetWidth,
        targetHeight
    )

    // 创建 canvas
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext('2d');
    ctx.putImageData(imageData, 0, 0);

    prettierLog({
        msg: 'postProcess end',
        type: 'success'
    });

    return canvas.toDataURL('image/png');
}

export const modelIsLoaded = () => {
    return model !== null;
}
export const doImageInPaint = async (image: HTMLImageElement, maskBase64: string, showAlert: any) => {
    return new Promise(async (resolve, reject) => {
        // console.log(image);
        if (!model) await createInferenceSession(showAlert);
        const {img, mask} = await preProcess(image, maskBase64);
        const results = await runModel(img, mask);

        const res = postProcess(results);
        resolve(res);
    })
}
